import axios from "axios";
import { API_BASE_URL } from '../../config/apiConfig';

// ==============================================
// EMPLOYEE TRANSFER APIs
// ==============================================

// 1. Get Employee Extender (GET) - Search employees by prefix
export const getEmployeeExtender = async (prefix) => {
    try {

        const response = await axios.get(
            `${API_BASE_URL}/HR/GetEmployeeExtender`,
            {
                params: { Prefix: prefix },
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to get employee extender';
    }
};

// 2. Get Employee CC Code (GET) - Get cost center code for an employee
export const getEmployeeCCCode = async (empId) => {
    try {

        const response = await axios.get(
            `${API_BASE_URL}/HR/GetEmployeeCCCode`,
            {
                params: { Empid: empId },
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to get employee CC code';
    }
};

// 3. Get All Employee Cost Centers (GET) - Get all CCs filtered by cc
export const getAllEmpCC = async (cc) => {
    try {

        const response = await axios.get(
            `${API_BASE_URL}/HR/GetAllEmpCC`,
            {
                params: { cc },
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to get all employee cost centers';
    }
};

// 4. Save Employee Transfer Details (POST) - Calls spSaveEmployeeTransferDetails
export const saveEmployeeTransferDetails = async (params) => {
    try {

        if (!params.employeeId)    throw new Error('Employee ID is required');
        if (!params.fromCC)        throw new Error('Relieving Cost Center is required');
        if (!params.toCC)          throw new Error('Joining Cost Center is required');
        if (!params.relievingDate) throw new Error('Relieving Date is required');
        if (!params.joiningDate)   throw new Error('Joining Date is required');
        if (!params.createdBy)     throw new Error('Created By is required');
        if (!params.roleId)        throw new Error('Role ID is required');

        const payload = {
            EmployeeId:   params.employeeId?.toString()    || '',
            FromCC:       params.fromCC?.toString()        || '',
            ToCC:         params.toCC?.toString()          || '',
            RelievingDate: params.relievingDate?.toString() || '',
            JoiningDate:  params.joiningDate?.toString()   || '',
            Remarks:      params.remarks?.toString()       || '',
            Createdby:    params.createdBy?.toString()     || '',
            RoleID:       parseInt(params.roleId),
        };


        const response = await axios.post(
            `${API_BASE_URL}/HR/SaveEmployeeTransferDetails`,
            payload,
            { headers: { 'Content-Type': 'application/json' } }
        );
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save employee transfer details';
    }
};

// 5. Get Verify Employee Transfer Grid (GET) - Inbox list by role
export const getVerifyEmployeeTransferGrid = async (roleId) => {
    try {

        const response = await axios.get(
            `${API_BASE_URL}/HR/VerifyEmployeeTransferGrid`,
            {
                params: { Roleid: roleId },
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to get verify employee transfer grid';
    }
};

// 6. Get Verify Employee Transfer View (GET) - Detail view of a single transfer record
export const getVerifyEmployeeTransferView = async (lvId) => {
    try {

        const response = await axios.get(
            `${API_BASE_URL}/HR/VerifyEmployeeTransferView`,
            {
                params: { Lvid: lvId },
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to get verify employee transfer view';
    }
};

// 7. Approve Employee Transfer (PUT) - Calls spApproveEmployeeTransfer
//
// ── FIELD NAME CONTRACT ───────────────────────────────────────────────────────
// The component (VerifyEmployeeTransfer.jsx) calls this function via the Redux
// thunk with the following payload shape (built in buildApprovalPayload):
//
//   { lid, EmployeeId, FromCC, ToCC, RelievingDate, JoiningDate,
//     Roleid, CreatedBy, Action, Note, Remarks }
//
// The SP (spApproveEmployeeTransfer) expects:
//   lid, Status, ReturnNotes, Createdby, RoleID
//
// This function is the translation layer between those two shapes.
// NEVER add validation for keys that are not in the incoming params shape.
// ─────────────────────────────────────────────────────────────────────────────
export const approveEmployeeTransfer = async (params) => {
    try {

        // ── Validate using the ACTUAL keys the component sends ────────────────
        if (!params.lid)       throw new Error('Record ID (lid) is required');
        if (!params.Action)    throw new Error('Action is required (Approve / Verify / Reject)');
        if (!params.CreatedBy) throw new Error('CreatedBy is required');
        if (!params.Roleid)    throw new Error('Roleid is required');

        // ── Map component field names → SP field names ────────────────────────
        //   params.Action    → Status       (SP param name)
        //   params.Note      → ReturnNotes  (SP param name)
        //   params.CreatedBy → Createdby    (SP param name, lowercase 'b')
        //   params.Roleid    → RoleID       (SP param name)
        const payload = {
            lid:         params.lid?.toString()      || '',
            Status:      params.Action?.toString()   || '',   // "Approve" | "Verify" | "Reject"
            ReturnNotes: params.Note?.toString()     || '',
            Createdby:   params.CreatedBy?.toString() || '',
            RoleID:      params.Roleid?.toString()   || '',
        };


        const response = await axios.put(
            `${API_BASE_URL}/HR/ApproveEmployeeTransfer`,
            payload,
            { headers: { 'Content-Type': 'application/json' } }
        );
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to approve employee transfer';
    }
};

// 8. Get Employee Extender Report (GET) - Report search by prefix
export const getEmployeeExtenderReport = async (prefix) => {
    try {

        const response = await axios.get(
            `${API_BASE_URL}/HR/GetEmployeeExtenderReport`,
            {
                params: { Prefix: prefix },
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to get employee extender report';
    }
};

// 9. Get Verify Employee Transfer Report View (GET) - Report detail view
export const getVerifyEmployeeTransferReportView = async (lvId) => {
    try {

        const response = await axios.get(
            `${API_BASE_URL}/HR/VerifyEmployeeTransferReportView`,
            {
                params: { Lvid: lvId },
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to get verify employee transfer report view';
    }
};